import { useRoleLabel } from '@/hooks/useRoleLabel';
import type { Role } from '@/types';
import { Accordion } from '@nautilusbio/nb-design-system/Accordion';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import React, { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';

import orgSelectorStyles from './styles.module.css';
import type { OrgListItemProps } from './types';

/** A single org row. The selected org is inert — its role is changed through the sidebar's
 *  RoleSwitcher, so a picker here would be two controls for one setting. See README.md. */
export const OrgListItem: FC<OrgListItemProps> = memo(
  ({ org, allowedRoles, assumedRole, isSelected, onSwitch, isPending }) => {
    const roleLabel = useRoleLabel();
    const { t: tOrgs } = useTranslation('orgs');
    // Never set by user interaction (see the role-button onPress below) — purely derived from props.
    const selectedRole: Role | null =
      assumedRole && allowedRoles.includes(assumedRole) ? assumedRole : (allowedRoles[0] ?? null);

    // aria-current rather than isDisabled: a native `disabled` would drop the row naming the user's
    // own org out of the tab order. React Aria's Button strips aria-disabled, so that is not an
    // option here — "current" is the truer semantic anyway, and it survives to the DOM.
    if (isSelected) {
      return (
        <Button aria-current="true" data-testid={`org-selector-item-${org.id}`} size="sm" visual="unstyled" fullWidth>
          <Stack
            alignItems="center"
            as="span"
            fullWidth
            gap={2}
            justifyContent="space-between"
            orientation="horizontal"
          >
            <Text as="span">{org.name}</Text>

            <Check aria-label={tOrgs('org_selector_current')} role="img" size={12} />
          </Stack>
        </Button>
      );
    }

    // Never vanish: a row the user cannot act in still tells them the org exists, which a silent
    // null does not. Reachable whenever the grant map omits an org the backend still returns.
    if (!allowedRoles.length || !selectedRole) {
      return (
        <Button data-testid={`org-selector-item-${org.id}`} isDisabled size="sm" visual="unstyled" fullWidth>
          <Stack
            alignItems="center"
            as="span"
            fullWidth
            gap={2}
            justifyContent="space-between"
            orientation="horizontal"
          >
            <Text as="span">{org.name}</Text>

            <Text as="span" size="xs">
              {tOrgs('org_selector_no_roles')}
            </Text>
          </Stack>
        </Button>
      );
    }

    const hasMultipleRoles = allowedRoles.length > 1;

    // If single role, just show a simple button without expansion
    if (!hasMultipleRoles) {
      return (
        <Button
          data-testid={`org-selector-item-${org.id}`}
          isDisabled={isPending}
          onPress={() => onSwitch(org, selectedRole)}
          size="sm"
          visual="unstyled"
          fullWidth
        >
          <Text as="span">{org.name}</Text>
        </Button>
      );
    }

    // Multiple roles: use Accordion for smooth expand/collapse. The trigger is a native <button>
    // (react-aria Disclosure) wrapping the whole `label`, so a second interactive control cannot
    // live inside it — org switching moved to the role buttons in the panel below (immediate on
    // press, same pattern as the sidebar's own RoleSwitcher), and this row now only expands.
    return (
      <Accordion
        data-testid={`org-selector-accordion-${org.id}`}
        // Below the popover's own h4 title; the DS default of 3 would outrank its own heading.
        headingLevel={5}
        label={
          <Stack as="span" alignItems="center" fullWidth gap={2} justifyContent="space-around" orientation="horizontal">
            <Stack
              as="span"
              className={clsx(getTextClassName('sm'), orgSelectorStyles.orgName)}
              data-testid={`org-selector-item-${org.id}`}
              fullWidth
            >
              {org.name}
            </Stack>

            <Text as="span" className={orgSelectorStyles.roleText} noWrap size="xs">
              {roleLabel(selectedRole)}
            </Text>
          </Stack>
        }
        gap={1}
        orientation="vertical"
        showChevron
        chevronPosition="end"
      >
        <Stack className={orgSelectorStyles.roleSelectorContainer} fullWidth gap={1} orientation="horizontal">
          {allowedRoles.map(role => {
            const isRoleSelected = role === selectedRole;

            return (
              <Button
                className={orgSelectorStyles.currentRoleOrgSelector}
                data-testid={`org-role-${org.id}-${role}`}
                isDisabled={isPending}
                key={role}
                // Not setSelectedRole first: this was an unrolled-back optimistic write — on a
                // failed switch (handleSwitchRole toasts and returns without closing the popover)
                // it left the row highlighting and labelling the role the user did NOT get.
                onPress={() => onSwitch(org, role)}
                size="xs"
                visual={isRoleSelected ? 'success' : 'outline'}
              >
                {roleLabel(role)}

                {isRoleSelected && <Check size={10} />}
              </Button>
            );
          })}
        </Stack>
      </Accordion>
    );
  },
);

OrgListItem.displayName = 'OrgListItem';
